<template>

<app-content :loading="is.loading">

	<app-content-head :title="title" back="Convention.Schedule.Suggestions" :readonly="$authorised('con/schedule/access', 'read')">

		<app-content-head-actions-item :disabled="!isDirty" icon="undo" text="Undo" v-on:click="onUndoClick" />
		<app-content-head-actions-item :loading="is.saving" :disabled="!isDirty || !isValid" icon="save" text="Save" v-on:click="onSaveClick" />

		<app-content-head-actions-item v-if="canDecide" :divide="true" text="Accept" icon="approve" :success="true" :disabled="isDirty || !canAccept" v-on:click="onAcceptClick" />
		<app-content-head-actions-item v-if="canDecide" text="Reject" icon="reject.thumb" :danger="true" :disabled="isDirty" v-on:click="onRejectClick" />

	</app-content-head>

	<app-content-body v-if="is.initialised">

        <app-content-summary>

			<app-content-summary-item label="Status" :tag="$constants.schedule.suggestions.statusName[item.status]" :status="$constants.schedule.suggestions.statusTheme[item.status]" :theme="$constants.schedule.suggestions.statusTheme[item.status]" />
			<app-content-summary-item v-if="isApplication" label="Applicant" :value="item.application.name" :link="linkApplication" />
			<app-content-summary-item v-if="!isApplication" label="Badge" :value="item.badge.name" :link="linkBadge" />
			<app-content-summary-item label="Event" :grow="true" :value="item.name" :link="linkEvent" />
			<app-content-summary-item label="Room" :value="room.name" v-if="room" />

			<app-content-summary-item v-if="canDecide" label="Schedule Preferences" class="preferences">

				<div v-for="(time, index) in item.preferred" :key="index" class="preferences-time">{{ $con.schedule.days[time.day].slice(0, 3) }} {{ $con.schedule.times[time.time] }} ({{ $con.schedule.durations[time.duration] }})</div>

			</app-content-summary-item>			
			
			<app-content-summary-item label="Submitted On" :value="item.date.created | localDate('MM/DD/YYYY h:mma')" />
			<app-content-summary-item v-if="item.date.decided" label="Decided On" :value="item.date.decided | localDate('MM/DD/YYYY h:mma')" />
			<app-content-summary-item v-if="item.date.scheduled" label="Scheduled On" :value="item.date.scheduled | localDate('MM/DD/YYYY h:mma')" />
			<app-content-summary-item v-if="!item.date.scheduled && isAccepted" label="Scheduling" :value="(item.autoschedule) ? 'Automatic' : 'User'" />

		</app-content-summary>

		<app-content-alert v-if="!item.room" theme="red" :is-inline="true" icon="warning">Please note you must select a room and save before being able to schedule a specific time and space.</app-content-alert>

		<app-content-box>

			<app-input-select v-if="!item.room" label="Room" placeholder="Select room" v-model="model.room" :options="$util.options(references.rooms, 'name', 'id')" />

			<app-input-scheduletime v-if="item.room" v-on:picker="is.picking = $event" :locked="isLocked" :has-picker="hasSpaces" :room="room" :spaces="spaces" :show-space="hasSpaces" label="Schedule" v-model="model" :is-custom="true" v-on:space="model.space = $event" v-on:day="model.day = $event" v-on:time="model.time = $event" v-on:duration="model.duration = $event" />

		</app-content-box>

		<app-content-box :tabs="contentTabs" :readonly="$authorised('con/schedule/access', 'read') || isLocked">

			<template v-slot:about>

				<app-input-suggest v-if="hasGame" v-on:textchange="onGameNameChange" label="Game" placeholder="Select game" image="image" v-model="model.game" api="bgg/game" :validation="$v.model.game" />
				<app-input-text label="Name" placeholder="Enter name..." v-model="model.name" :validation="$v.model.name" />
				<app-input-text label="Description" :autogrow="true" placeholder="Enter description..." v-model="model.event.description" :validation="$v.model.event.description" />
				<app-input-image label="Image" v-model="model.event.image" :validation="$v.model.event.image" />
			
			</template>

			<template v-slot:labels>

				<app-input-select v-if="$_.keys(references.types).length" label="Type" placeholder="Select type" v-model="model.type" :validation="$v.model.type" :options="references.types" />
				<app-input-toggle :full-width="true" label="Min. Age" :slim="true" v-model="model.event.age" :validation="$v.model.event.age" :options="ageOptions" />
				<app-input-toggle :full-width="true" v-if="hasGame" label="Min. Experience" :slim="true" v-model="model.event.experience" :validation="$v.model.event.experience" :options="expOptions" :tooltips="expTooltips" />
				<app-input-toggle :full-width="true" label="Teacher Wanted" v-model="model.event.teacher" :validation="$v.model.event.teacher" v-if="hasGame" />
				<app-input-checklist v-if="$_.keys(references.lobbies).length" label="Lobbies" v-model="model.event.lobbies" :validation="$v.model.event.lobbies" :options="references.lobbies" />

			</template>

			<template v-slot:seats>

				<app-input-toggle v-if="hasGame" :full-width="true" label="Host playing" :options="hostOptions" v-model="model.event.host" :validation="$v.model.event.host" />
				<app-input-text :label="(model.ticket) ? 'Total' : 'Total seats'" placeholder="Enter number..." v-model="model.event.seats" :validation="$v.model.event.seats" />
				<app-input-text v-if="hasGame" label="Blocked seats" placeholder="Enter number..." v-model="model.event.blocked" :validation="$v.model.event.blocked" />

			</template>

			<template v-slot:time>

				<app-input-text label="Location" placeholder="Enter directions..." v-model="model.event.location" :validation="$v.model.event.location" />
				
			</template>

			<template v-slot:admin>

				<app-input-text label="Custom Host" maxlength="32" :toggle="model.event.hostname.enabled" v-on:toggle="model.event.hostname.enabled = !model.event.hostname.enabled" notes="Customise the name of the host to something other than the user that created the event." placeholder="Enter name..." v-model="model.event.hostname.name" :validation="$v.model.event.hostname.name" />
				<app-input-toggle label="Official" v-model="model.event.official" :validation="$v.model.event.official" />
				<app-input-toggle label="Enable Clashing" v-model="model.event.clashes.enabled" :validation="$v.model.event.clashes.enabled" />
				<app-input-select label="Ticketed" v-model="model.ticket" :allow-deselect="true" placeholder="Not ticketed" :validation="$v.model.ticket" :options="references.tickets" />

			</template>

		</app-content-box>

		<app-content-box>

			<app-input-text label="Private Notes" :autogrow="true" placeholder="Enter any notes" notes="This note will never be shared with the user." v-model="model.note.admin" />

		</app-content-box>

		<app-widget-chat v-if="!isApplication" title="Submission Discussion" :id="item.id" :locked="isScheduled" />
		
	</app-content-body>

	<app-spaces v-if="is.picking" v-on:close="is.picking = false" :is-picking="is.picking" :room="room" :preferred="item.preferred" :spaces="references.spaces" :scheduled="references.scheduled" />

</app-content>

</template>

<script>

import mixForm from '@/mixin/form'

export default {

	mixins: [mixForm],

	data: function() {

		return {
			is: {
				picking: false
			},
			readonly: this.$authorised('con/schedule/access', 'read'),
			references: {
				rooms: [],
				spaces: [],
				scheduled: [],
				types: [],
				lobbies: []
			},
			filter: {
				search: '',
				days: 1,
				times: 0
			},
			model: {	
				id: '',
				day: 0,
				time: 0,
				name: '',
				duration: 0,
				room: '',
				type: '',
				space: '',
				ticket: '',
				game: '',
				event: {
					seats: 0,
					host: 0,
					description: '',
					location: '',
					blocked: 0,
					official: 0,
					teacher: 0,
					experience: 0,
					age: 0,
					image: '',
					lobbies: [],
					hostname: {
						enabled: 0,
						name: ''
					},
					clashes: {
						enabled: 0
					}
				},
				note: {
					user: '',
					admin: ''
				}
			},
            hostOptions: {
                0: 'User is playing',
                1: 'User is hosting/teaching/organizing'
            }
		}

	},

	validations: {
		model: {
			event: {
				hostname: {},
				clashes: {}
			}
		}
	},

	computed: {

		selectedType: function() {

			return (this.model.type) ? this.$_.findWhere(this.references.types, {
				id: this.model.type
			}) : false

		},

		hasGame: function() {

			return (this.selectedType) ? this.selectedType.games.enabled : true

		},

		contentTabs: function() {

			var tabs = [
				{ name: 'about', text: 'About' },
				{ name: 'labels', text: 'Labels' },
				{ name: 'seats', text: (this.model.ticket) ? 'Tickets' : 'Seats' }
			];

			if (!this.hasSpaces) tabs.push({ name: 'time', text: 'Location' })

			tabs.push({ name: 'admin', text: 'Settings' })

			return tabs

		},

		ageOptions: function() {

			return this.$constants.schedule.ageShort

		},

		expOptions: function() {

			return this.$constants.schedule.experienceShort

		},

		expTooltips: function() {

			return this.$constants.schedule.experienceTooltip

		},

		hasSpaces: function() {

			return (this.room) ? this.room.spaces : false
		
		},

		canAccept: function() {
		
			return (this.hasSpaces) ? this.model.space : (this.model.time && this.model.duration && this.model.day)

		},

		wasAccepted: function() {

			return this.$_.contains([
				this.$constants.schedule.suggestions.status.approved,
				this.$constants.schedule.suggestions.status.preapproved,
				this.$constants.schedule.suggestions.status.cancelled
			], this.item.status)
		
		},

		isLocked: function() {

			return this.$_.contains([
				this.$constants.schedule.suggestions.status.cancelled,
				this.$constants.schedule.suggestions.status.rejected,
				this.$constants.schedule.suggestions.status.scheduled
			], this.item.status)
		
		},

		isAccepted: function() {

			return this.$_.contains([
				this.$constants.schedule.suggestions.status.approved,
				this.$constants.schedule.suggestions.status.preapproved
			], this.item.status)
		
		},

		isApplication: function() {
		
			return this.$_.contains([
				this.$constants.schedule.suggestions.status.applied,
				this.$constants.schedule.suggestions.status.preapproved
			], this.item.status)
		
		},

		isScheduled: function() {
		
			return this.$_.contains([
				this.$constants.schedule.suggestions.status.scheduled
			], this.item.status)
		
		},

		canDecide: function() {
		
			return this.$_.contains([
				this.$constants.schedule.suggestions.status.submitted,
				this.$constants.schedule.suggestions.status.applied
			], this.item.status)
		
		},

		linkApplication: function() {
		
			return (this.item.application) ? {
				name: 'Convention.Registration.Applications.Edit',
				params: {
					id: this.item.application.id
				}
			} : false
		
		},

		linkEvent: function() {
		
			return (this.item.event) ? {
				name: 'Convention.Schedule.Events.Edit',
				params: {
					id: this.item.event
				}
			} : false

		},

		linkBadge: function() {
		
			return (this.item.badge) ? {
				name: 'Convention.Badges.Holders.Edit',
				params: {
					id: this.item.badge.id
				}
			} : false
		
		},

		rooms: function() {
		
			return this.references.rooms

		},

		spaces: function() {

			return this.references.spaces
		
		},

		room: function() {
		
			return this.$_.findWhere(this.references.rooms, {
				id: this.model.room
			})

		},

		space: function() {
		
			return this.$_.findWhere(this.references.spaces, {
				id: this.model.space
			})

		},

		highlighted: function() {
		
			var highlighted = {}

			this.$_.each(this.days, function(day, value) {
			
				highlighted[value] = []
			
			})

			this.$_.each(this.item.preferred, function(preference) {
			
				highlighted[preference.day].push({
					time: parseInt(preference.time),
					duration: parseInt(preference.duration)
				})

			})

			return highlighted

		},

		scheduled: function() {
		
			return this.references.scheduled

		},

		times: function() {
		
			return this.$con.schedule.times

		},

		days: function() {

			var days = {}

			var preferredDays = this.$_.pluck(this.item.preferred, 'day')

			this.$_.each(this.$con.schedule.days, function(day, value) {

				if (!this.filter.days || this.$_.contains(preferredDays, value)) days[value] = day
			
			}.bind(this))

			return days

		}
	
	},

	methods: {

		onGameNameChange: function(name) {

			this.model.name = name

		},
	
		onAcceptClick: function() {

			this.$router.push({
				name: 'Convention.Schedule.Suggestion.Accept'
			})
		
		},

		onRejectClick: function() {

			this.$router.push({
				name: 'Convention.Schedule.Suggestion.Reject'
			})
		
		}
	
	}

}

</script>

<style scoped>

.suggestion-spaces {
    height: auto;
    padding: 0px;
    margin-bottom: 10px;
    border: 1px solid #eee;
    border-radius: 4px;
    overflow: auto;
    min-height: 100px;
}

.suggestion-spaces:last-child {
	margin-bottom: 0px;
}

.suggestion-spaces >>> .body {
    padding: 0px!important;
}

.suggestion-spaces >>> .row {
    padding-right: 0px;
}

.preferences >>> .item-value {
    display: flex;
}

.preferences-time {
	font-size: 14px;
    text-align: center;
	padding: 5px 6px;
	line-height: 18px;
	font-weight: 400;
	border-radius: 4px;
	background-color: #fff;
    border: 1px solid #eee;
    margin-right: 4px;
}

.suggestion-filter {
	display: flex;
	align-items: center;
}

.suggestion-filter-search {
	width: 240px;
}

.suggestion-filter-text {
	font-size: 16px;
	color: #687589;
	margin-left: 10px;
	margin-right: 10px;
	font-weight: 500;
	flex-grow: 1;
}

.suggestion-filter >>> .input {
	margin-bottom: 0px;
	margin-right: 40px;
}

.suggestion-filter >>> .input:last-child {
	margin-right: 10px;
}

.suggestion-filter >>> .input-label {
	width: auto;
	margin-right: 10px;
}

</style>